import { mapPickerSections } from '../../lib/mapPickerOptions'
import { WarfishClassicsSectionHeader } from '../map/warfish/WarfishClassicsHelp'
/**
 * MapPickerModal — the full-screen map picker: every map as a uniform tile
 * with a mini preview, standard maps first, then special-rules boards and the
 * Warfish classics. The setup page's map card opens it ("Change map").
 *
 * "Inspect" delegates upward via `onInspectMap` so the parent can close the
 * modal, navigate to the map details, and re-open on return.
 */

import { COMPACT_HEADER_STYLE } from '../../lib/gameChrome'
import React, { useState } from 'react'
import { View, Text, TouchableOpacity, ScrollView } from 'react-native'
import { AppModal } from '../ui/AppModal'
import { Button } from '../ui/Button'
import { useSafeAreaInsets } from 'react-native-safe-area-context'
import { MapThumbnail } from '../map/MapThumbnail'
import { NewMapTag } from '../map/NewMapTag'

/** Map tiles per row in the picker, and the gap between them. */
export const MAP_PICKER_COLUMNS = 2
export const MAP_PICKER_GAP = 10
/** Picker content cap, so two tiles never stretch across a desktop window. */
export const MAP_PICKER_MAX_WIDTH = 760

export { mapPickerSections } from '../../lib/mapPickerOptions'

/** Width of one tile so MAP_PICKER_COLUMNS tiles plus gaps fill `gridWidth`. */
export function mapPickerTileWidth(gridWidth: number): number {
  if (!(gridWidth > 0)) return 0
  return Math.floor((gridWidth - MAP_PICKER_GAP * (MAP_PICKER_COLUMNS - 1)) / MAP_PICKER_COLUMNS)
}

interface Props {
  mapId: string
  /** `specialRules` maps get their own section after the standard ones. */
  availableMaps: { id: string; name: string; specialRules?: boolean; isNew?: boolean; legacy?: 'warfish' }[]
  onChangeMap: (mapId: string) => void
  onInspectMap?: (mapId: string) => void
  /**
   * Controlled: the parent closes the modal before navigating to
   * map-details, then re-opens it on return (useReopenOnFocus).
   */
  open: boolean
  onOpenChange: (open: boolean) => void
  /** Replaces Done's default (just close), e.g. save and leave the screen. */
  onDone?: () => void
  /** Accessibility label for Done when `onDone` changes what it does. */
  doneLabel?: string
}

export function MapPickerModal({
  mapId,
  availableMaps,
  onChangeMap,
  onInspectMap,
  open,
  onOpenChange: setOpen,
  onDone,
  doneLabel,
}: Props) {
  const [gridWidth, setGridWidth] = useState(0)
  const tileWidth = mapPickerTileWidth(gridWidth)
  const sections = mapPickerSections(availableMaps)
  const insets = useSafeAreaInsets()

  const mapName = availableMaps.find((m) => m.id === mapId)?.name ?? mapId

  return (
    <AppModal
      visible={open}
      animationType="slide"
      transparent={false}
      onRequestClose={() => setOpen(false)}
    >
      <View
        className="flex-1 bg-canvas"
        // Safe-area insets on all four edges so the header clears
        // the status bar / Dynamic Island in portrait and the
        // notch in either landscape rotation. Bottom inset lets
        // the sticky Done button float above the home indicator.
        style={{
          paddingTop: insets.top,
          paddingLeft: insets.left,
          paddingRight: insets.right,
        }}
      >
        <View className="flex-row items-center justify-between px-4 border-b border-olive" style={COMPACT_HEADER_STYLE}>
          <Text className="text-ink font-stencil text-lg uppercase">Select Map</Text>
          <TouchableOpacity
            onPress={() => setOpen(false)}
            accessibilityLabel="Close map picker"
            accessibilityRole="button"
            testID="map-picker-close"
            hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}
            className="border-2 border-olive rounded-full items-center justify-center"
            style={{ width: 36, height: 36 }}
          >
            <Text className="text-ink font-stencil text-base">×</Text>
          </TouchableOpacity>
        </View>

        <ScrollView contentContainerStyle={{ padding: 16, paddingBottom: 32, gap: 16, width: '100%', maxWidth: MAP_PICKER_MAX_WIDTH, alignSelf: 'center' }}>
          {/* Uniform two-column tiles with mini previews: standard maps
              first, then boards with their own special rules. */}
          <View testID="map-picker-grid" onLayout={(event) => setGridWidth(Math.round(event.nativeEvent.layout.width))} style={{ gap: 16 }}>
            {tileWidth > 0 && sections.map((section) => (
              <View key={section.key} testID={`map-picker-section-${section.key}`} style={{ gap: 6 }}>
                {section.key === 'warfish' ? <WarfishClassicsSectionHeader helpTestID="map-picker-warfish-help" /> : section.title && (
                  <Text accessibilityRole="header" className="font-stencil text-[10px] uppercase tracking-widest text-olive-up">
                    {section.title}
                  </Text>
                )}
                <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: MAP_PICKER_GAP }}>
                  {section.maps.map((m) => {
                    const selected = mapId === m.id
                    return (
                      <TouchableOpacity
                        key={m.id}
                        onPress={() => onChangeMap(m.id)}
                        accessibilityLabel={`Select map ${m.name}`}
                        accessibilityRole="button"
                        accessibilityState={{ selected }}
                        testID={`map-picker-tile-${m.id}`}
                        className={`rounded-lg border-2 p-2 flex-row items-center gap-2 ${
                          selected ? 'bg-blood/20 border-blood' : 'bg-paper-up border-olive'
                        }`}
                        style={{ width: tileWidth, minHeight: 60 }}
                      >
                        <MapThumbnail mapId={m.id} width={56} height={42} />
                        <View className="flex-1" style={{ gap: 2 }}>
                          <Text
                            className={`font-stencil text-xs ${selected ? 'text-blood' : 'text-ink'}`}
                            numberOfLines={2}
                          >
                            {m.name}
                          </Text>
                          {m.isNew && <NewMapTag testID={`map-picker-new-${m.id}`} style={{ alignSelf: 'flex-start' }} />}
                        </View>
                      </TouchableOpacity>
                    )
                  })}
                </View>
              </View>
            ))}
          </View>
        </ScrollView>

        {/* Sticky red Done button at the bottom — matches the
            pattern Players + Game Setup screens already use, so
            the host has a one-tap dismiss path that doesn't
            require reaching the upper-right of the screen.
            insets.bottom padding lifts it above the home indicator. */}
        <View
          className="border-t border-olive bg-canvas"
          style={{
            paddingHorizontal: 16,
            paddingTop: 12,
            paddingBottom: 12 + insets.bottom,
          }}
        >
          {/* Inspect (secondary) on its own row above the primary Done. */}
          <View style={{ gap: 10 }}>
            {onInspectMap && (
              <Button
                title={`Inspect ${mapName}`}
                variant="secondary"
                onPress={() => {
                  setOpen(false)
                  onInspectMap(mapId)
                }}
                accessibilityLabel={`Inspect ${mapName}`}
              />
            )}
            <Button
              title="Done"
              onPress={onDone ?? (() => setOpen(false))}
              accessibilityLabel={doneLabel ?? 'Done — back to lobby'}
            />
          </View>
        </View>
      </View>
    </AppModal>
  )
}
